'use client';

import { useSyncExternalStore } from 'react';
import Script from 'next/script';
import { hasAnalyticsConsent } from '@/lib/analytics/consent';

// EWM-760 — consent doesn't change mid-session without a reload (a fresh
// consent decision re-runs this module on the next page load), so subscribe
// is a no-op; useSyncExternalStore just swaps the SSR default (false, so the
// server renders the deterministic `<noscript>`-only markup) for the real
// client cookie read after hydration — same timing as the old mount effect.
const subscribe = () => () => {};
const getServerSnapshot = () => false;

// EWM-269 — Google Tag Manager install. Per Luke (2026-05-28) the
// dataLayer events from EWM-243 (`lib/analytics/dataLayer.ts`) feed
// these GTM containers, which then forward to GA4:
//   AU: GTM-KR2Z9QMH   NZ: GTM-P6DSRFT9
//
// Two concerns sit in this component:
//
//   1. Consent gating. The GTM bootstrap script itself sends a network
//      call to googletagmanager.com — privacy-relevant. We mount the
//      `<Script>` only after `hasAnalyticsConsent()` returns true, so a
//      user who declined the cookie banner doesn't load GTM at all.
//      Events pushed to `window.dataLayer` while GTM is unloaded still
//      queue on the array; GTM picks them up on a later page-load if
//      consent flips. The dataLayer wrapper double-gates pushes on
//      consent anyway, so nothing fires either way without acceptance.
//
//   2. SSR/hydration. Consent is read from a client cookie, so on the
//      server we render only the deterministic `<noscript>` markup;
//      `<Script>` mounts on the client after the consent check. The
//      noscript fallback is rendered unconditionally — a user with JS
//      disabled can't interact with the consent banner either way, and
//      omitting it would defeat the install instruction.

interface GtmInitProps {
  /** GTM container id (e.g. `GTM-KR2Z9QMH`). Falsy → renders nothing. */
  containerId?: string;
}

const GtmInit = ({ containerId }: GtmInitProps) => {
  const consented = useSyncExternalStore(subscribe, hasAnalyticsConsent, getServerSnapshot);

  if (!containerId) return null;

  return (
    <>
      {consented && (
        <Script id="gtm-head" strategy="afterInteractive">
          {`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${containerId}');`}
        </Script>
      )}
      <noscript>
        <iframe
          src={`https://www.googletagmanager.com/ns.html?id=${containerId}`}
          title="Google Tag Manager"
          height="0"
          width="0"
          style={{ display: 'none', visibility: 'hidden' }}
        />
      </noscript>
    </>
  );
};

export default GtmInit;
